import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../../services/api';

const RULE_TYPES = [
  { id: 'exact', label: 'Exact email' },
  { id: 'domain', label: 'Domain' },
  { id: 'contains', label: 'Contains' },
];

export default function EmailSuppression() {
  const [rules, setRules] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Creating a rule
  const [scope, setScope] = useState('dealer'); // dealer | site
  const [siteId, setSiteId] = useState('');
  const [ruleType, setRuleType] = useState('exact');
  const [value, setValue] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const canSelectSite = (sites || []).length > 1;

  const siteOptions = useMemo(() => {
    return (sites || []).map(s => ({ id: s.id, name: `${s.name} (${s.slug})` }));
  }, [sites]);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [sitesRes, rulesRes] = await Promise.all([
        api.getSites(),
        api.listEmailSuppressionRules(),
      ]);
      setSites(sitesRes || []);
      setRules(rulesRes || []);
    } catch (e) {
      setError(e?.message || 'Failed to load suppression rules');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // If dealer only has a single site, force dealer-wide rules (no extra UI complexity).
  useEffect(() => {
    if (!canSelectSite) {
      setScope('dealer');
      setSiteId('');
    }
  }, [canSelectSite]);

  const onCreate = async (e) => {
    e.preventDefault();

    const finalSiteId = scope === 'site' && canSelectSite ? (siteId || null) : null;
    if (scope === 'site' && canSelectSite && !finalSiteId) {
      setError('Please select a site');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      await api.createEmailSuppressionRule({
        rule_type: ruleType,
        value,
        site_id: finalSiteId,
      });
      setValue('');
      await load();
    } catch (e2) {
      setError(e2?.message || 'Failed to create rule');
    } finally {
      setSubmitting(false);
    }
  };

  const onDelete = async (ruleId) => {
    if (!window.confirm('Delete this suppression rule?')) return;
    setError(null);
    try {
      await api.deleteEmailSuppressionRule(ruleId);
      await load();
    } catch (e) {
      setError(e?.message || 'Failed to delete rule');
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>🚫 Email Suppression</h2>
        <div style={styles.subtitle}>
          Block creating/updating contacts when the email matches a rule (exact, domain, or contains).
        </div>
      </div>

      {error && (
        <div style={styles.errorBox}>
          {error}
        </div>
      )}

      <div style={styles.card}>
        <h3 style={styles.cardTitle}>Add Rule</h3>
        <form onSubmit={onCreate} style={styles.form}>
          <label style={styles.label}>
            Scope
            {canSelectSite ? (
              <select
                value={scope}
                onChange={(e) => setScope(e.target.value)}
                style={styles.select}
                disabled={submitting}
              >
                <option value="dealer">All sites (dealer-wide)</option>
                <option value="site">Specific site</option>
              </select>
            ) : (
              <div style={styles.scopePill}>All sites (dealer-wide)</div>
            )}
          </label>

          {canSelectSite && scope === 'site' && (
            <label style={styles.label}>
              Site
              <select
                value={siteId}
                onChange={(e) => setSiteId(e.target.value)}
                style={styles.select}
                disabled={submitting}
              >
                <option value="">Select…</option>
                {siteOptions.map(opt => (
                  <option key={opt.id} value={opt.id}>{opt.name}</option>
                ))}
              </select>
            </label>
          )}

          <label style={styles.label}>
            Type
            <select
              value={ruleType}
              onChange={(e) => setRuleType(e.target.value)}
              style={styles.select}
              disabled={submitting}
            >
              {RULE_TYPES.map(t => (
                <option key={t.id} value={t.id}>{t.label}</option>
              ))}
            </select>
          </label>

          <label style={styles.label}>
            Value
            <input
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={ruleType === 'domain' ? 'example.com' : ruleType === 'contains' ? 'test+' : 'user@example.com'}
              style={styles.input}
              disabled={submitting}
            />
          </label>

          <button
            type="submit"
            style={{
              ...styles.button,
              ...(submitting ? styles.buttonDisabled : {}),
            }}
            disabled={submitting}
          >
            {submitting ? 'Adding…' : 'Add Rule'}
          </button>
        </form>
      </div>

      <div style={styles.card}>
        <h3 style={styles.cardTitle}>Rules</h3>
        {loading ? (
          <div style={styles.loading}>Loading…</div>
        ) : rules.length === 0 ? (
          <div style={styles.empty}>No suppression rules yet.</div>
        ) : (
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Scope</th>
                  <th style={styles.th}>Type</th>
                  <th style={styles.th}>Value</th>
                  <th style={styles.th}>Created</th>
                  <th style={styles.th}></th>
                </tr>
              </thead>
              <tbody>
                {rules.map(r => {
                  const siteName = r.site_id
                    ? (sites.find(s => s.id === r.site_id)?.name || r.site_id)
                    : 'All sites (dealer-wide)';
                  return (
                    <tr key={r.id}>
                      <td style={styles.td}>{siteName}</td>
                      <td style={styles.td}><code style={styles.code}>{r.rule_type}</code></td>
                      <td style={styles.td}><code style={styles.code}>{r.value}</code></td>
                      <td style={styles.td}>{(r.created_at || '').slice(0, 19).replace('T', ' ')}</td>
                      <td style={styles.tdRight}>
                        <button style={styles.dangerBtn} onClick={() => onDelete(r.id)}>
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: {
    maxWidth: 1000,
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    color: '#f1f5f9',
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  title: {
    margin: 0,
    fontSize: 22,
    fontWeight: 700,
  },
  subtitle: {
    color: '#94a3b8',
    fontSize: 14,
  },
  card: {
    background: '#111827',
    border: '1px solid #334155',
    borderRadius: 12,
    padding: 16,
  },
  cardTitle: {
    margin: '0 0 12px 0',
    fontSize: 16,
    fontWeight: 600,
  },
  form: {
    display: 'grid',
    gridTemplateColumns: '1.2fr 1.2fr 1.2fr 2fr auto',
    gap: 12,
    alignItems: 'end',
  },
  label: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    fontSize: 13,
    color: '#cbd5e1',
  },
  scopePill: {
    height: 40,
    borderRadius: 10,
    border: '1px solid #334155',
    background: '#0b1220',
    color: '#e2e8f0',
    padding: '0 12px',
    display: 'flex',
    alignItems: 'center',
    fontSize: 13,
  },
  input: {
    height: 40,
    borderRadius: 10,
    border: '1px solid #334155',
    background: '#0b1220',
    color: '#f1f5f9',
    padding: '0 12px',
    outline: 'none',
  },
  select: {
    height: 40,
    borderRadius: 10,
    border: '1px solid #334155',
    background: '#0b1220',
    color: '#f1f5f9',
    padding: '0 12px',
    outline: 'none',
  },
  button: {
    height: 40,
    borderRadius: 10,
    border: 'none',
    background: 'var(--brand-primary, #3b82f6)',
    color: '#fff',
    padding: '0 14px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  buttonDisabled: {
    opacity: 0.7,
    cursor: 'not-allowed',
  },
  dangerBtn: {
    height: 34,
    borderRadius: 10,
    border: '1px solid #7f1d1d',
    background: '#450a0a',
    color: '#fecaca',
    padding: '0 12px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  tableWrap: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    textAlign: 'left',
    fontSize: 12,
    color: '#94a3b8',
    padding: '10px 8px',
    borderBottom: '1px solid #334155',
  },
  td: {
    padding: '10px 8px',
    borderBottom: '1px solid #1f2937',
    fontSize: 13,
    color: '#e2e8f0',
    verticalAlign: 'top',
  },
  tdRight: {
    padding: '10px 8px',
    borderBottom: '1px solid #1f2937',
    textAlign: 'right',
  },
  code: {
    background: '#0b1220',
    border: '1px solid #334155',
    padding: '2px 6px',
    borderRadius: 8,
    fontSize: 12,
    color: '#e2e8f0',
  },
  errorBox: {
    background: '#3f1d1d',
    border: '1px solid #7f1d1d',
    borderRadius: 12,
    padding: 12,
    color: '#fecaca',
    fontSize: 14,
  },
  loading: {
    color: '#94a3b8',
    fontSize: 14,
  },
  empty: {
    color: '#94a3b8',
    fontSize: 14,
  },
};
